import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, AlertTriangle, X } from 'lucide-react';
import { BacktestResult } from '@/lib/portfolio-optimizer-api';

interface BacktestModalProps {
  isOpen: boolean;
  onClose: () => void;
  strategy: string;
  holdings: Array<{
    mint: string;
    uiAmount: number;
    valueUsd: number;
  }>;
  backtestResults?: BacktestResult;
  isLoading?: boolean;
  error?: string;
}

export default function BacktestModal({
  isOpen,
  onClose,
  strategy,
  holdings,
  backtestResults,
  isLoading = false,
  error,
}: BacktestModalProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  // Prepare chart data
  const chartData = backtestResults?.portfolio_values.map((value, index) => ({
    date: `Day ${index + 1}`,
    value: value,
  })) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-6xl max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto" aria-modal="true" role="dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Backtest Results - {strategy}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Loading State */}
          {isLoading && (
            <div className="space-y-4">
              <Skeleton className="h-8 w-full" />
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-20" />
                ))}
              </div>
              <Skeleton className="h-64" />
              <Skeleton className="h-48" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Results */}
          {backtestResults && !isLoading && !error && (
            <>
              {/* Performance Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle>Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Total Return</p>
                      <p className={`text-2xl font-bold ${backtestResults.performance_metrics.total_return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(backtestResults.performance_metrics.total_return)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Annualized Return</p>
                      <p className={`text-2xl font-bold ${backtestResults.performance_metrics.annualized_return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(backtestResults.performance_metrics.annualized_return)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Volatility</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {formatPercent(backtestResults.performance_metrics.volatility)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Sharpe Ratio</p>
                      <p className="text-2xl font-bold">
                        {backtestResults.performance_metrics.sharpe_ratio.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Max Drawdown</p>
                      <p className="text-2xl font-bold text-red-600">
                        {formatPercent(backtestResults.performance_metrics.max_drawdown)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Portfolio Value Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Portfolio Value Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis tickFormatter={formatCurrency} />
                      <Tooltip formatter={(value: number) => [formatCurrency(value), 'Portfolio Value']} />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#8884d8"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Trade History Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Trade History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Date</th>
                          <th className="text-left py-2">Asset</th>
                          <th className="text-center py-2">Action</th>
                          <th className="text-right py-2">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backtestResults.trades.map((trade, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-2">{new Date(trade.date).toLocaleDateString()}</td>
                            <td className="py-2">{trade.asset.slice(0, 8)}...</td>
                            <td className="text-center py-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                trade.action === 'BUY' ? 'bg-green-100 text-green-800' :
                                trade.action === 'SELL' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {trade.action}
                              </span>
                            </td>
                            <td className="text-right py-2">{formatCurrency(trade.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}